import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import DashboardShell from './DashboardShell';
import './CoachDashboard.css';

const sanitizePhoneNumber = (value) => String(value || '').replace(/[^\d]/g, '');
const COACH_REFLECTION_FORM_URL = process.env.REACT_APP_COACH_REFLECTION_FORM_URL || '';

const CoachDashboard = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('overview');
  const [userData, setUserData] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);

  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const supportNumber = useMemo(
    () => sanitizePhoneNumber(process.env.REACT_APP_SUPPORT_WHATSAPP_NUMBER),
    []
  );

  const formatMalayDate = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(`${isoDate}T00:00:00`);
    const days = ['Ahad', 'Isnin', 'Selasa', 'Rabu', 'Khamis', 'Jumaat', 'Sabtu'];
    const months = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogos', 'Sep', 'Okt', 'Nov', 'Dis'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hh, mm] = timeStr.split(':');
    const hour = Number(hh);
    if (Number.isNaN(hour)) return timeStr;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${displayHour}:${mm} ${ampm}`;
  };

  const getAttendanceTone = (rate) => {
    if (rate === null || rate === undefined || Number.isNaN(Number(rate))) return 'pending';
    const value = Number(rate);
    if (value >= 90) return 'good';
    if (value >= 75) return 'warn';
    return 'bad';
  };

  const getAttendanceLabel = (rate) => {
    if (rate === null || rate === undefined || Number.isNaN(Number(rate))) return 'Belum disambung';
    return `${Math.round(Number(rate))}%`;
  };

  const firstName = useMemo(() => {
    const name = userData?.fullName || 'Jurulatih';
    return name.trim().split(' ')[0] || 'Jurulatih';
  }, [userData]);

  const upcomingSchedules = useMemo(() => {
    return schedules
      .filter((schedule) => schedule.date && schedule.date >= todayISO)
      .sort((a, b) => {
        const dateCompare = (a.date || '').localeCompare(b.date || '');
        if (dateCompare !== 0) return dateCompare;
        return (a.startTime || '').localeCompare(b.startTime || '');
      });
  }, [schedules, todayISO]);

  const nextSchedule = upcomingSchedules[0] || null;
  const nextScheduleLabel = nextSchedule
    ? `${formatMalayDate(nextSchedule.date)} / ${formatTime(nextSchedule.startTime)}${nextSchedule.endTime ? ` - ${formatTime(nextSchedule.endTime)}` : ''}`
    : '';
  const attendanceRate = null; // Ready to connect with published Google Sheet attendance data.
  const attendanceTone = getAttendanceTone(attendanceRate);
  const coachSupportUrl = useMemo(() => {
    if (!supportNumber) return '';
    const coachName = userData?.fullName || 'jurulatih';
    const location = [userData?.daerah, userData?.terminal].filter(Boolean).join(' / ') || 'lokasi saya';
    const message = `Assalamualaikum BTOP, saya ${coachName}. Saya ingin maklumkan status sesi jurulatih untuk ${location}.`;
    return `https://wa.me/${supportNumber}?text=${encodeURIComponent(message)}`;
  }, [supportNumber, userData]);

  useEffect(() => {
    const fetchUser = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }

      const snap = await getDoc(doc(db, 'users', user.uid));
      if (!snap.exists()) {
        navigate('/login');
        return;
      }

      const data = { uid: user.uid, ...snap.data() };
      if (data.role !== 'coach') {
        navigate(`/dashboard/${data.role}`);
        return;
      }

      setUserData(data);
    };

    fetchUser();
  }, [navigate]);

  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        setLoadingSchedules(true);
        const schedulesRef = collection(db, 'schedules');
        const schedulesQuery = query(schedulesRef, where('active', '==', true));
        const snap = await getDocs(schedulesQuery);
        const coachName = String(userData?.fullName || '').toLowerCase();

        const list = snap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((schedule) => {
            const type = String(schedule.type || '').toLowerCase();
            const subject = String(schedule.subject || '').toLowerCase();
            const teacher = String(schedule.teacher || '').toLowerCase();
            const isCoachActivity =
              type.includes('play') ||
              type.includes('workshop') ||
              subject.includes('play') ||
              subject.includes('bengkel') ||
              subject.includes('workshop') ||
              teacher.includes('coach');

            const isAssignedCoach = coachName ? teacher.includes(coachName) : false;
            return isCoachActivity || isAssignedCoach;
          })
          .sort((a, b) => {
            const dateCompare = (a.date || '').localeCompare(b.date || '');
            if (dateCompare !== 0) return dateCompare;
            return (a.startTime || '').localeCompare(b.startTime || '');
          });

        setSchedules(list);
      } catch (error) {
        console.error('fetchCoachSchedules error:', error);
      } finally {
        setLoadingSchedules(false);
      }
    };

    if (userData?.uid) fetchSchedules();
  }, [userData]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const navItems = [
    { key: 'overview', label: 'Utama', onClick: () => setActiveView('overview') },
    { key: 'classes', label: 'Kelas', onClick: () => setActiveView('classes') },
    { key: 'reflection', label: 'Refleksi', onClick: () => setActiveView('reflection') },
    { key: 'notes', label: 'Nota', onClick: () => setActiveView('notes') },
  ];

  return (
    <DashboardShell
      brand="BTOP Wellness"
      subtitle="Papan Pemuka Jurulatih"
      roleLabel="Jurulatih"
      userName={userData?.fullName || 'Jurulatih'}
      userInitial={userData?.fullName?.charAt(0)?.toUpperCase() || 'C'}
      navItems={navItems}
      activeKey={activeView}
      onLogout={handleLogout}
      tone="green"
    >
      <main className="coach-dashboard">
        {activeView === 'overview' && (
          <>
            <section className="coach-card hero">
              <p>Ruang Kerja Jurulatih</p>
              <h1>{firstName}, sesi seterusnya sudah disusun.</h1>
              <span>Paparan utama ini membantu jurulatih menyemak sesi akan datang dan menyediakan catatan refleksi selepas aktiviti.</span>

              <div className="coach-next-card">
                <div>
                  <small>Kelas Seterusnya</small>
                  <strong>{loadingSchedules ? 'Memuatkan jadual...' : nextSchedule?.subject || 'Belum ada kelas akan datang'}</strong>
                  <span>
                    {nextSchedule
                      ? `${nextScheduleLabel} / ${nextSchedule.schoolName || nextSchedule.daerah || '-'} / ${nextSchedule.terminal || '-'}`
                      : 'Jadual jurulatih akan muncul apabila pentadbir menambah aktiviti.'}
                  </span>
                </div>
              </div>
            </section>

            <section className={`coach-card attendance ${attendanceTone}`}>
              <div>
                <p>Status Kehadiran</p>
                <h2>{getAttendanceLabel(attendanceRate)}</h2>
                <span>
                  {attendanceTone === 'good'
                    ? 'Status baik: 90% ke atas.'
                    : attendanceTone === 'warn'
                    ? 'Perlu perhatian: 75% hingga 89%.'
                    : attendanceTone === 'bad'
                    ? 'Kritikal: bawah 75%.'
                    : 'Data kehadiran jurulatih belum disambungkan kepada Google Sheet.'}
                </span>
              </div>
              <small>Hijau 90%+ / Kuning 75-89% / Merah &lt;75%</small>
            </section>

            <section className="coach-card feedback">
              <div>
                <p>Maklum Balas</p>
                <h2>Refleksi berstruktur selepas sesi</h2>
                <span>
                  {nextSchedule
                    ? `Sediakan catatan refleksi untuk ${nextSchedule.subject || 'aktiviti'} pada ${nextScheduleLabel}.`
                    : 'Belum ada aktiviti untuk pautkan refleksi.'}
                </span>
              </div>
              {coachSupportUrl ? (
                <a href={coachSupportUrl} target="_blank" rel="noreferrer">
                  WhatsApp BTOP
                </a>
              ) : (
                <button type="button" disabled={!nextSchedule}>
                  Tambah catatan
                </button>
              )}
            </section>
          </>
        )}

        {activeView === 'classes' && (
          <section className="coach-card coach-classes">
            <div className="coach-section-head">
              <div>
                <p>Kelas Jurulatih</p>
                <h2>Aktiviti Play & Talk</h2>
              </div>
              <small>{loadingSchedules ? 'Memuatkan...' : `${schedules.length} aktiviti`}</small>
            </div>

            {loadingSchedules ? (
              <div className="coach-empty-state">Memuatkan kelas jurulatih...</div>
            ) : upcomingSchedules.length === 0 ? (
              <div className="coach-empty-state">
                <strong>Belum ada aktiviti akan datang</strong>
                <span>Jadual jurulatih akan muncul apabila pentadbir menambah sesi Play & Talk atau bengkel.</span>
              </div>
            ) : (
              <div className="coach-class-list">
                {upcomingSchedules.slice(0, 6).map((schedule) => (
                  <article key={schedule.id} className="coach-class-item">
                    <div className="coach-class-time">
                      <strong>{formatTime(schedule.startTime) || '-'}</strong>
                      <span>{schedule.endTime ? formatTime(schedule.endTime) : 'Tamat belum ada'}</span>
                    </div>
                    <div className="coach-class-body">
                      <div className="coach-class-title">
                        <h3>{schedule.subject || schedule.type || 'Aktiviti Jurulatih'}</h3>
                        <small>{schedule.type || 'Aktiviti'}</small>
                      </div>
                      <p>{formatMalayDate(schedule.date)}</p>
                      <div className="coach-meta-row">
                        <span>{schedule.schoolName || schedule.daerah || '-'}</span>
                        <span>{schedule.terminal || '-'}</span>
                        {schedule.room ? <span>Bilik {schedule.room}</span> : null}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {activeView === 'notes' && (
          <section className="coach-card coach-notes">
            <p>Nota Sesi</p>
            <h2>Pemerhatian dan tindakan susulan</h2>
            <span>Ruang ini digunakan untuk menyusun pemerhatian sesi, keperluan sokongan dan catatan jurulatih.</span>

            <div className="coach-note-grid">
              <article>
                <small>Pemerhatian</small>
                <strong>Catat perubahan tingkah laku dan penglibatan pelajar selepas aktiviti.</strong>
              </article>
              <article>
                <small>Tindakan susulan</small>
                <strong>Senaraikan pelajar atau kelas yang perlukan perhatian sesi seterusnya.</strong>
              </article>
            </div>
          </section>
        )}

        {activeView === 'reflection' && (
          <section className="coach-card coach-reflection">
            <p>Refleksi Jurulatih</p>
            <h2>Nilai perjalanan aktiviti Play & Talk</h2>
            <span>Gunakan borang khas jurulatih untuk merekod penglibatan pelajar, respons aktiviti, cabaran dan tindakan susulan.</span>

            <div className="coach-reflection-guide">
              <article><strong>Penglibatan</strong><span>Nilai respons dan penyertaan pelajar.</span></article>
              <article><strong>Pemerhatian</strong><span>Catat perubahan atau keperluan sokongan.</span></article>
              <article><strong>Susulan</strong><span>Nyatakan tindakan untuk sesi akan datang.</span></article>
            </div>

            {COACH_REFLECTION_FORM_URL ? (
              <a className="coach-reflection-open" href={COACH_REFLECTION_FORM_URL} target="_blank" rel="noreferrer">
                Buka Borang Refleksi Jurulatih
              </a>
            ) : (
              <div className="coach-reflection-unavailable">
                Pautan borang belum ditetapkan. Pentadbir perlu mengisi REACT_APP_COACH_REFLECTION_FORM_URL.
              </div>
            )}
          </section>
        )}
      </main>
    </DashboardShell>
  );
};

export default CoachDashboard;
